import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSigningMethods } from './helperfunction';
import { useAtom } from 'jotai';
import { userAtom } from '../atom';
import { useTheme } from '../contexts/themeContext';
import SitsComLogo from './SitsComLogo';
import { motion, AnimatePresence } from 'framer-motion';

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.25, ease: [0.25, 0.1, 0.25, 1], when: 'beforeChildren', staggerChildren: 0.06 } },
  exit: { opacity: 0, transition: { duration: 0.2, ease: 'easeIn', when: 'afterChildren', staggerChildren: 0.03, staggerDirection: -1 } },
};

const headerVariants = {
  hidden: { opacity: 0, y: -12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.15 } },
};

const linkVariants = {
  hidden: { opacity: 0, x: -24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] } },
  exit: { opacity: 0, x: 16, transition: { duration: 0.15 } },
};

const footerVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] } },
  exit: { opacity: 0, y: 8, transition: { duration: 0.15 } },
};

const ThemeIcon = ({ theme, size = 16 }) =>
  theme === 'light' ? (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );

export default function Menu({ isAdmin }) {
  const { handleLogout } = useSigningMethods();
  const user = useAtom(userAtom)?.[0];
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/home', label: 'Subjects' },
    { path: '/feedback', label: 'Feedback' },
    { path: '/about', label: 'About' },
  ];

  const allLinks = [
    ...navLinks,
    ...(isAdmin ? [
      { path: '/admin', label: 'Admin' },
      { path: '/addData', label: 'Add Data' },
    ] : []),
  ];

  return (
    <>
      <nav className="nav">
        <div className="nav-inner">
          <Link to="/" className="nav-brand">
            <SitsComLogo size={26} />
            SitsCom
          </Link>

          <ul className="nav-links">
            {allLinks.map(({ path, label }) => (
              <li key={path}>
                <Link to={path} className={isActive(path) ? 'active' : ''}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="nav-actions">
            <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme" title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}>
              <ThemeIcon theme={theme} />
            </button>

            {user ? (
              <div className="nav-user" onClick={handleLogout} title="Log out">
                <div className="user-avatar">
                  {user.displayName?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <span>{user.displayName?.split(' ')[0] || 'User'}</span>
              </div>
            ) : (
              <Link to="/signin" className="btn btn-primary btn-sm">
                Sign in
              </Link>
            )}

            <button className="nav-mobile-toggle" onClick={() => setMobileOpen(true)} aria-label="Open menu">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12h18M3 6h18M3 18h18" />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="nav-mobile-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <motion.div className="nav-mobile-header" variants={headerVariants}>
              <Link to="/" className="nav-brand" onClick={() => setMobileOpen(false)}>
                <SitsComLogo size={26} />
                SitsCom
              </Link>
              <button className="nav-mobile-close" onClick={() => setMobileOpen(false)} aria-label="Close menu">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </motion.div>

            <div className="nav-mobile-body">
              {allLinks.map(({ path, label }, i) => (
                <motion.div key={path} variants={linkVariants} custom={i}>
                  <Link to={path} className={isActive(path) ? 'active' : ''} onClick={() => setMobileOpen(false)}>
                    {label}
                  </Link>
                </motion.div>
              ))}
            </div>

            <motion.div className="nav-mobile-footer" variants={footerVariants}>
              <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
                <ThemeIcon theme={theme} size={18} />
              </button>

              {user ? (
                <button className="btn btn-primary btn-sm" onClick={() => { handleLogout(); setMobileOpen(false); }}>
                  Log out
                </button>
              ) : (
                <Link to="/signin" className="btn btn-primary btn-sm" onClick={() => setMobileOpen(false)}>
                  Sign in
                </Link>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
